import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AssetDto, GalleryDto } from '@picflow/types';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { calcBaseUrl } from '../../../lib/calc-base-url';
import { calcResizedUrl } from '../../../lib/calc-resized-url';
import { AppConfigService } from '../../../providers/config.service';
import { OnGetData } from '../../../services/dialog.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { CopyClipboardDirective } from '../../../shared-extra/directives/copy-to-clipboard';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { ShareDirective } from '../../../shared-extra/directives/share.directive';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';

@Component({
  selector: 'app-create-cdn-link-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './create-cdn-link-dialog.component.html',
  styleUrl: './create-cdn-link-dialog.component.scss',
  imports: [
    SvgComponent,
    ButtonDirective,
    SvgComponent,
    CopyClipboardDirective,
    InputFieldComponent,
    ShareDirective,
    InputPostDirective,
    ButtonMenuItemComponent,
    ContextMenuComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class CreateCdnLinkDialogComponent implements OnGetData {
  private check = inject(ChangeDetectorRef);
  public state = inject(StateService);
  private toasts = inject(ToastService);
  private config = inject(AppConfigService);
  dialogName = 'create-cdn-link';

  dialogOptions(): DialogOptions {
    return { spacingX: 40 };
  }

  close: (...args) => void;
  gallery: GalleryDto;
  asset: AssetDto;
  cdnLink: string;
  canShare = navigator.share && ScreenSizes.screenSize() !== 'desktop';
  selectedLongEdge: { longEdge: number; name: string };
  presetSizes = [
    {
      longEdge: 720,
      name: 'Small',
    },
    {
      longEdge: 1920,
      name: 'Medium',
    },
    {
      longEdge: 3840,
      name: 'Large',
    },
    {
      longEdge: undefined,
      name: 'Original',
    },
  ];

  sizeForm = new FormGroup({
    width: new FormControl(),
    height: new FormControl(),
  });

  onGetData(data: { asset: AssetDto; gallery: GalleryDto }, close: (...args) => void): void {
    this.close = close;
    this.asset = data.asset;
    this.gallery = data.gallery;

    const assetLongEdge = this.assetLongEdge;
    this.presetSizes.find(size => size.name === 'Original').longEdge = assetLongEdge;
    this.presetSizes = this.presetSizes.filter(size => size.longEdge <= assetLongEdge);
    this.selectedLongEdge = this.presetSizes.find(size => size.longEdge === 1920) || this.presetSizes[0];

    this.autoCalc();

    this.sizeForm.valueChanges.subscribe(() => this.calculateCdnLink());

    this.calculateCdnLink();
    this.check.detectChanges();
  }

  private autoCalc() {
    if (this.selectedLongEdge) {
      this.sizeForm.get('width').disable();
      this.sizeForm.get('height').disable();
      if (this.aspectRatio > 1) {
        this.sizeForm.get('width').setValue(this.selectedLongEdge.longEdge);
        this.sizeForm.get('height').setValue(this.calcHeightFromWidth());
      } else {
        this.sizeForm.get('height').setValue(this.selectedLongEdge.longEdge);
        this.sizeForm.get('width').setValue(this.calcWidthFromHeight());
      }
    } else {
      this.sizeForm.get('width').enable();
      this.sizeForm.get('height').enable();
    }
  }

  onLinkCopied() {
    this.toasts.add('Link copied', 'success');
  }

  calcWidthFromHeight() {
    return Math.round(this.sizeForm.get('height').value * this.aspectRatio);
  }

  calcHeightFromWidth() {
    return Math.round(this.sizeForm.get('width').value / this.aspectRatio);
  }

  setLongEdge(size: (typeof this.presetSizes)[0]) {
    this.selectedLongEdge = size;
    this.autoCalc();
    this.check.detectChanges();
  }

  private get aspectRatio() {
    return this.asset.version_data.data.width / this.asset.version_data.data.height;
  }

  private get assetLongEdge() {
    return Math.min(this.asset.version_data.data.width, this.asset.version_data.data.height);
  }

  autoCalcHeight() {
    this.sizeForm.get('height').setValue(Math.round(this.calcHeightFromWidth()));
    this.check.detectChanges();
  }

  autoCalcWidth() {
    this.sizeForm.get('width').setValue(Math.round(this.calcWidthFromHeight()));
    this.check.detectChanges();
  }

  async calculateCdnLink() {
    const width = Math.round(this.sizeForm.get('width').value);
    const height = Math.round(this.sizeForm.get('height').value);

    if (width > this.asset.version_data.data.width) {
      this.cdnLink = calcBaseUrl(this.config.assetsEndpoint, this.asset.version_data.uuid, this.asset.version_data.ext);
    } else {
      this.cdnLink = calcResizedUrl(
        this.config.assetsEndpoint,
        this.asset.version_data.uuid,
        `${width}x${height}`,
        true
      );
    }
  }
}
